import React from 'react';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import SEO from '../components/SEO';
import { ExternalLink, CheckCircle2 } from 'lucide-react';

const directories = [
  { name: 'Google Business Profile', url: 'https://business.google.com', priority: 'CRITICAL', desc: 'The single most important listing for local SEO. Verify, add all services, photos, and post weekly.' },
  { name: 'Bing Places', url: 'https://www.bingplaces.com', priority: 'HIGH', desc: 'Powers Bing + ChatGPT search citations.' },
  { name: 'Apple Business Connect', url: 'https://businessconnect.apple.com', priority: 'HIGH', desc: 'Feeds Apple Maps and Siri.' },
  { name: 'Yelp for Business', url: 'https://biz.yelp.com', priority: 'HIGH', desc: 'Trust signal + backlink.' },
  { name: 'Facebook Business Page', url: 'https://www.facebook.com/business', priority: 'HIGH', desc: 'Social signal + traffic.' },
  { name: 'Better Business Bureau', url: 'https://www.bbb.org', priority: 'HIGH', desc: 'High-authority trust signal.' },
  { name: 'Clutch.co', url: 'https://clutch.co', priority: 'HIGH', desc: 'The #1 directory for web design agencies. Get client reviews here.' },
  { name: 'DesignRush', url: 'https://www.designrush.com', priority: 'HIGH', desc: 'Design agency directory — free listing available.' },
  { name: 'GoodFirms', url: 'https://www.goodfirms.co', priority: 'MEDIUM', desc: 'Web design agency directory.' },
  { name: 'The Manifest', url: 'https://themanifest.com', priority: 'MEDIUM', desc: 'B2B services directory (owned by Clutch).' },
  { name: 'UpCity', url: 'https://upcity.com', priority: 'MEDIUM', desc: 'Local service provider directory.' },
  { name: 'Foursquare', url: 'https://business.foursquare.com', priority: 'MEDIUM', desc: 'Feeds many secondary directories automatically.' },
  { name: 'YellowPages', url: 'https://www.yellowpages.com', priority: 'MEDIUM', desc: 'Legacy but still crawled.' },
  { name: 'Manta', url: 'https://www.manta.com', priority: 'LOW', desc: 'Small business directory.' },
  { name: 'Hotfrog', url: 'https://www.hotfrog.com', priority: 'LOW', desc: 'Free business directory.' },
];

const outreach = [
  { title: 'Guest posts on marketing blogs', desc: 'Pitch “How med spas can grow with better web design” to sites like Medical Spa MD, Aesthetic Everything, American Med Spa Association blog.' },
  { title: 'Podcast interviews', desc: 'Book yourself on niche podcasts (The Aesthetic Business Coach, The Tattoo Marketing Podcast) — each episode = a dofollow backlink.' },
  { title: 'HARO / Qwoted / Featured', desc: 'Answer daily journalist queries for high-authority press mentions.' },
  { title: 'Local Chamber of Commerce', desc: 'Miami Beach + surrounding chambers — dofollow local backlinks.' },
  { title: 'Client case studies + testimonials', desc: 'Ask happy clients for a written testimonial on their site linking back to yours.' },
  { title: 'Sponsor a local event', desc: 'Sponsor a tattoo convention or aesthetic industry event — sponsor logos usually link back.' },
  { title: 'Partnerships & referrals', desc: 'Partner with photographers, brand designers, PR firms — swap logo placements.' },
  { title: 'Wikipedia / Wikidata entity', desc: 'Not for the business, but for the CEO — helps AI search understand who you are.' },
];

const aiSearch = [
  'Publish a detailed About page with clear entity facts (who, what, where, when).',
  'Add FAQ + How-To schema on every service page (we already did).',
  'Get cited on Wikipedia-adjacent knowledge sites (Crunchbase, LinkedIn Company).',
  'Publish original data / studies (surveys of med spas, tattoo shop growth) — AI loves stats.',
  'Answer Reddit and Quora questions in your niche with a link back to a resource on your site.',
  'Get listed on “best web design agencies for [niche]” roundup articles.',
  'Add llms.txt file (we did) so AI crawlers know exactly what your site is about.',
];

const PriorityBadge = ({ level }) => {
  const map = {
    CRITICAL: 'bg-red-100 text-red-700 border-red-200',
    HIGH: 'bg-[#D9A04A]/15 text-[#B17A24] border-[#D9A04A]/30',
    MEDIUM: 'bg-blue-100 text-blue-700 border-blue-200',
    LOW: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full border ${map[level]}`}>{level}</span>;
};

const BacklinksPage = () => (
  <Layout>
    <SEO
      title="Backlink & Citation Strategy | Website Symphony"
      description="The complete citation, directory and outreach checklist Website Symphony uses to help clients rank on Google and appear in AI search results like ChatGPT and Perplexity."
      canonical="/backlinks"
      noIndex
    />
    <PageHero title="Backlink & Citation Playbook" subtitle="The exact list of directories, citations and outreach targets to build authority for Website Symphony and rank on both Google and AI search." breadcrumb="Backlinks" />

    <section className="py-16 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <div className="bg-[#D9A04A]/10 border-l-4 border-[#D9A04A] p-6 rounded-r-lg mb-12">
          <p className="text-[#2C5F66] font-semibold mb-2">How to use this page</p>
          <p className="text-gray-700 text-sm leading-relaxed">Backlinks and citations cannot be created programmatically — they must be earned or claimed externally. This page is the full checklist. Work through it top-to-bottom (critical first). Every claim uses <strong>identical NAP</strong>: <br /><em>Website Symphony — 420 NE 67th St, Suite 711-North, Miami Beach, FL 33141 — (305) 812-5999 — Info@WebsiteSymphony.com</em>.</p>
        </div>

        <h2 className="text-[#2C5F66] text-2xl md:text-3xl font-extrabold mb-6">1. Business Directories &amp; Citations</h2>
        <div className="space-y-3 mb-16">
          {directories.map((d) => (
            <a key={d.name} href={d.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 bg-[#F7F7F5] hover:bg-white hover:shadow-md rounded-lg p-4 transition-all group">
              <CheckCircle2 className="text-[#2C5F66] mt-1 flex-shrink-0" size={20} />
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-[#2C5F66] font-bold group-hover:text-[#D9A04A] transition-colors">{d.name}</h3>
                  <PriorityBadge level={d.priority} />
                </div>
                <p className="text-gray-600 text-sm mt-1">{d.desc}</p>
              </div>
              <ExternalLink className="text-gray-400 group-hover:text-[#D9A04A] flex-shrink-0 mt-1" size={18} />
            </a>
          ))}
        </div>

        <h2 className="text-[#2C5F66] text-2xl md:text-3xl font-extrabold mb-6">2. Outreach &amp; Earned Backlinks</h2>
        <div className="grid md:grid-cols-2 gap-4 mb-16">
          {outreach.map((o) => (
            <div key={o.title} className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="text-[#2C5F66] font-bold mb-2">{o.title}</h3>
              <p className="text-gray-600 text-sm">{o.desc}</p>
            </div>
          ))}
        </div>

        <h2 className="text-[#2C5F66] text-2xl md:text-3xl font-extrabold mb-6">3. Ranking in AI Search (ChatGPT, Perplexity, Gemini)</h2>
        <ul className="space-y-3 mb-16">
          {aiSearch.map((tip, i) => (
            <li key={i} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-4">
              <span className="w-7 h-7 rounded-full bg-[#2C5F66] text-white font-bold flex items-center justify-center text-sm flex-shrink-0">{i + 1}</span>
              <span className="text-gray-700 text-sm leading-relaxed">{tip}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  </Layout>
);

export default BacklinksPage;
